import React, { useState, useEffect, useRef } from "react";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaGoogle,
  FaFacebook,
  FaTwitter,
  FaCar
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import ForgotPasswordPage from './ForgotPasswordPage';
import { auth } from "./utils/firebase";
import {
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ensureUserDoc } from "./utils/firestoreHelpers";

interface LoginPageProps {
  onLoginSuccess: () => void;
  onSwitchToSignup?: () => void;
  onForgotPassword?: () => void;
  onClose?: () => void;
}

// Enhanced error mapping function
function getFriendlyErrorMessage(error: any): string {
  if (!error) return "An unknown error occurred. Please try again.";

  const code = error.code || "";
  const message = error.message || "";

  switch (code) {
    case "auth/invalid-credential":
      return "Invalid email or password. Please check your credentials and try again.";
    case "auth/invalid-email":
      return "The email address is not valid. Please enter a valid email.";
    case "auth/user-disabled":
      return "This account has been disabled. Please contact support.";
    case "auth/user-not-found":
      return "No account found with this email. Please sign up first.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again or reset your password.";
    case "auth/too-many-requests":
      return "Too many login attempts. Please try again later or reset your password.";
    case "auth/operation-not-allowed":
      return "This login method is not enabled. Please try another method.";
    case "auth/email-already-in-use":
      return "This email is already in use. Please try logging in.";
    case "auth/weak-password":
      return "Password is too weak. Please choose a stronger password.";
    case "auth/account-exists-with-different-credential":
      return "An account already exists with this email but different sign-in method.";
    case "auth/invalid-verification-code":
      return "The OTP code is invalid. Please try again.";
    case "auth/invalid-phone-number":
      return "The phone number is invalid. Please check the format.";
    case "auth/missing-verification-code":
      return "Please enter the OTP code.";
    case "auth/captcha-check-failed":
      return "Security check failed. Please try again.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/popup-closed-by-user":
      return "Login popup was closed before completing. Please try again.";
    default:
      // For unhandled errors, show a generic message but log the full error
      console.error("Unhandled auth error:", error);
      return message || "An unexpected error occurred. Please try again.";
  }
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, onSwitchToSignup,  }) => {
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loginMethod, setLoginMethod] = useState<"password" | "otp">("password");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const confirmationResult = useRef<ConfirmationResult | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (validationMessage) {
      const timer = setTimeout(() => setValidationMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [validationMessage]);

  // This will be called when Android injects the token
  useEffect(() => {
    (window as any).TKS = {
      loginWithGoogleToken: async (idToken: string) => {
        try {
          const response = await fetch("/api/auth/custom-token", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ idToken }),
          });

          const data = await response.json();

          if (!data.customToken) throw new Error(data.message || "No custom token returned");

          // Sign in with Firebase custom token
          await signInWithCustomToken(auth, data.customToken);
          const user = auth.currentUser;

          if (user) {
            await ensureUserDoc(user);

            localStorage.setItem("user", JSON.stringify({
              uid: user.uid,
              fullName: user.displayName || "",
              email: user.email || "",
              mobile: user.phoneNumber || "",
              profileImage: user.photoURL || "",
            }));

            onLoginSuccess();
          }
        } catch (err) {
          console.error("❌ Web login with Google token failed", err);
          setValidationMessage("Google login failed. Please try again.");
        }
      },
    };
  }, [onLoginSuccess]);

  const validateLoginIdentifier = (identifier: string) => {
    if (loginMethod === "otp") {
      if (!/^(\+91)\d{10}$/.test(identifier)) {
        setValidationMessage("Please enter a valid 10-digit mobile number with +91 prefix");
        return false;
      }
    } else {
      if (identifier.startsWith("+91")) {
        if (!/^(\+91)\d{10}$/.test(identifier)) {
          setValidationMessage("Please enter a valid mobile number (+91 followed by 10 digits)");
          return false;
        }
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(identifier)) {
          setValidationMessage("Please enter a valid email address (e.g., user@example.com)");
          return false;
        }
      }
    }
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationMessage("");

    if (!validateLoginIdentifier(loginIdentifier)) {
      setIsLoading(false);
      return;
    }

    try {
      if (loginMethod === "password") {
        if (!password) {
          setValidationMessage("Please enter your password");
          setIsLoading(false);
          return;
        }

        const result = await signInWithEmailAndPassword(auth, loginIdentifier, password);
        await ensureUserDoc(result.user);

        localStorage.setItem("user", JSON.stringify({
          uid: result.user.uid,
          fullName: result.user.displayName || "",
          email: result.user.email || "",
          mobile: result.user.phoneNumber || "",
          profileImage: result.user.photoURL || "",
        }));

        setSubmitted(true);
        onLoginSuccess();
      } else {
        if (!otp) {
          setValidationMessage("Please enter the 6-digit OTP sent to your mobile");
          setIsLoading(false);
          return;
        }

        if (!confirmationResult.current) {
          setValidationMessage("OTP session expired. Please request a new OTP.");
          setIsLoading(false);
          return;
        }

        const result = await confirmationResult.current.confirm(otp);
        if (result?.user) {
          await ensureUserDoc(result.user);
          localStorage.setItem("user", JSON.stringify({
            uid: result.user.uid,
            fullName: result.user.displayName || "",
            email: result.user.email || "",
            mobile: result.user.phoneNumber || "",
            profileImage: result.user.photoURL || "",
          }));

          setSubmitted(true);
          onLoginSuccess();
        }
      }
    } catch (error: any) {
      const friendlyError = getFriendlyErrorMessage(error);
      setValidationMessage(friendlyError);
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!/^(\+91)\d{10}$/.test(loginIdentifier)) {
      setValidationMessage("Please enter a valid 10-digit mobile number with +91 prefix");
      return;
    }

    setIsLoading(true);
    setValidationMessage("");

    try {
      const recaptcha = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => {},
      });

      confirmationResult.current = await signInWithPhoneNumber(
        auth, 
        loginIdentifier, 
        recaptcha
      );

      setOtpSent(true);
      setOtpCooldown(30);
      
      const interval = setInterval(() => {
        setOtpCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      setValidationMessage("OTP sent successfully to your mobile number");
    } catch (error: any) {
      const friendlyError = getFriendlyErrorMessage(error);
      setValidationMessage(friendlyError);
      console.error("OTP send error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook' | 'twitter') => {
    setIsLoading(true);
    setValidationMessage("");

    try {
      let authProvider;
      switch (provider) {
        case 'google':
          authProvider = new GoogleAuthProvider();
          break;
        case 'facebook':
          authProvider = new FacebookAuthProvider();
          break;
        case 'twitter':
          authProvider = new TwitterAuthProvider();
          break;
        default:
          throw new Error('Invalid provider');
      }

      const result = await signInWithPopup(auth, authProvider);
      await ensureUserDoc(result.user);

      localStorage.setItem("user", JSON.stringify({
        uid: result.user.uid,
        fullName: result.user.displayName || "",
        email: result.user.email || "",
        mobile: result.user.phoneNumber || "",
        profileImage: result.user.photoURL || "",
      }));

      onLoginSuccess();
    } catch (error: any) {
      const friendlyError = getFriendlyErrorMessage(error);
      setValidationMessage(friendlyError);
      console.error(`${provider} login error:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  if (showForgotPassword) {
    return <ForgotPasswordPage onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-500 to-purple-600 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="bg-white p-10 rounded-2xl shadow-2xl w-96 text-center z-10"
      >
        <motion.h1
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-3xl font-extrabold text-gray-800 mb-2"
        >
          THE KARIGAR STOP
        </motion.h1>
        <h2 className="text-xl font-semibold text-gray-600 mb-6">Welcome Back</h2>

        <form onSubmit={handleLogin}>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative mb-4"
          >
            <FaUser className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              placeholder={loginMethod === "otp" ? "Mobile Number (+91XXXXXXXXXX)" : "Email or Mobile (+91XXXXXXXXXX)"}
              value={loginIdentifier}
              onChange={(e) => {
                let value = e.target.value;
                if (loginMethod === "otp") {
                  value = value.replace(/\D/g, '');
                  if (value === '') value = '+91';
                  else if (value.startsWith('91') && !value.startsWith('+91')) value = '+91' + value.substring(2);
                  else if (!value.startsWith('+91')) value = '+91' + value;
                  if (value.length > 13) value = value.substring(0, 13);
                }
                setLoginIdentifier(value);
              }}
              maxLength={loginMethod === "otp" ? 13 : undefined}
              className="w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
            />
          </motion.div>

          <div className="flex justify-center mb-4">
            <button
              type="button"
              onClick={() => {
                setLoginMethod("password");
                setOtpSent(false);
                setLoginIdentifier("");
                setValidationMessage("");
              }}
              className={`mx-2 font-semibold ${
                loginMethod === "password"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-blue-500"
              }`}
            >
              Password Login
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginMethod("otp");
                setLoginIdentifier("+91");
                setValidationMessage("");
              }}
              className={`mx-2 font-semibold ${
                loginMethod === "otp"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-blue-500"
              }`}
            >
              OTP Login
            </button>
          </div>

          {loginMethod === "password" ? (
            <>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="relative mb-2"
              >
                <FaLock className="absolute left-3 top-3 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-md"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </motion.div>
              <div className="text-right mb-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-blue-600 hover:underline text-sm font-medium"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </>
          ) : (
            <>
              {!otpSent ? (
                <motion.button
                  type="button"
                  onClick={handleSendOtp}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition duration-300 mb-4"
                  disabled={otpCooldown > 0 || loginIdentifier.length !== 13 || isLoading}
                >
                  {isLoading ? "Sending..." : 
                   otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Send OTP"}
                </motion.button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="relative mb-2"
                >
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(value);
                    }}
                    className="w-full pl-4 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 shadow-md"
                  />
                  <div className="text-right mt-2">
                    <button
                      type="button"
                      className="text-green-600 hover:underline"
                      onClick={handleSendOtp}
                      disabled={otpCooldown > 0}
                    >
                      {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                </motion.div>
              )}
            </>
          )}

          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="remember"
              className="mr-2"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <label htmlFor="remember" className="text-gray-600">Remember Me</label>
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition duration-300"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="inline-block animate-pulse">Processing...</span>
            ) : loginMethod === "password" ? "Login" : "Verify OTP"}
          </motion.button>

          <p className="text-gray-600 mt-4">Or continue with</p>
          <div className="flex justify-center gap-4 mt-3">
            <motion.button
              type="button"
              whileHover={{ scale: 1.1 }}
              className="bg-red-500 text-white p-3 rounded-full shadow-lg hover:bg-red-600"
              onClick={() => handleSocialLogin('google')}
              disabled={isLoading}
            >
              <FaGoogle />
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.1 }}
              className="bg-blue-700 text-white p-3 rounded-full shadow-lg hover:bg-blue-800"
              onClick={() => handleSocialLogin('facebook')}
              disabled={isLoading}
            >
              <FaFacebook />
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.1 }}
              className="bg-blue-400 text-white p-3 rounded-full shadow-lg hover:bg-blue-500"
              onClick={() => handleSocialLogin('twitter')}
              disabled={isLoading}
            >
              <FaTwitter />
            </motion.button>
          </div>

          <p className="text-gray-600 mt-4">
            Don't have an account?
            <button
              type="button"
              onClick={() => {
                if (onSwitchToSignup) onSwitchToSignup();
                else navigate("/signup");
              }}
              className="text-blue-600 hover:underline ml-1 focus:outline-none"
              disabled={isLoading}
            >
              Sign up
            </button>
          </p>

          <div className="mt-2 min-h-[24px]">
            <AnimatePresence>
              {validationMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={`text-sm p-3 rounded-lg ${
                    validationMessage.includes("successfully") 
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {validationMessage}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </form>
      </motion.div>

      {submitted && (
        <motion.div
          initial={{ x: "-100%" }}
          animate={{ x: "110%" }}
          transition={{ duration: 2, ease: "easeInOut" }}
          className="absolute top-1/2 transform -translate-y-1/2"
          onAnimationComplete={() => setSubmitted(false)}
        >
          <FaCar size={48} className="text-gray-800" />
        </motion.div>
      )}

      <div id="recaptcha-container"></div>
    </div>
  );
};

export default LoginPage;